import { reactive } from 'vue';
import { holdVueDialogs, runWithVueDialogs, toPlainText } from './legacyDialogs';
import { log } from '@/utils/core/vueLogger';
import { ECOS_APP } from '@/utils/domain/portalModel';

// The Vue portal (src/components/portal/PortalPage.vue) over the legacy portal page (app/pages/home/home.js).
// That page keeps running hidden in the iframe for its side effects, which the application relies on and
// nothing else produces yet: the sales options (externis.sfaActive gates the whole SFA synchronization),
// externis.applications / fileHelper, the sl.json settings (ag-Grid licence, Helpjuice secret, AppInsights)
// and the SSO handoff. Vue snapshots externis.vmHome while the legacy shows that page and calls its own
// click handler, so a tile does exactly what it did (md/ROUTER_MIGRATION.md "Phase 2c").

const POLL_MS = 500;
const ACCESS_RIGHTS_KEY = 'APP_ACCESS_RIGHTS';
// An application that never takes over (eCOS logon stuck, blocked navigation) gives the tiles back.
const OPENING_TIMEOUT_MS = 30000;

const initialState = () => ({ loaded: false, ready: false, title: '', apps: [], news: [], assortAllowed: true, opening: null });

export const legacyPortal = reactive(initialState());

let win = null;
let active = false;
let pollTimer = null;
let openingTimer = null;
let releaseDialogs = null;
// APP_ACCESS_RIGHTS is read when the applications show (the previous session's rights, home.js
// _applyOfflineAppVisibility) and again once the logons are done (__successAssort saved the new ones).
let rightsReads = 0;

const safe = (fn, fallback = null) => {
	try {
		const value = fn();
		return value === undefined ? fallback : value;
	} catch {
		return fallback;
	}
};

const vmHome = () => safe(() => win.externis.vmHome);
const legacyStorage = () => safe(() => (win.require.defined('storage') ? win.require('storage') : null));

// Plain copies: live KO models never enter Vue reactivity.
const plainApp = (app) => ({ id: app?.id ?? null, reference: app?.reference ?? '', label: app?.label ?? '', imageUrl: app?.imageUrl ?? '' });
const plainNews = (item) => safe(() => JSON.parse(JSON.stringify(item)), null);

function assign(key, value) {
	if (JSON.stringify(legacyPortal[key]) !== JSON.stringify(value)) legacyPortal[key] = value;
}

function readAccessRights() {
	rightsReads += 1;
	const current = win;
	safe(() =>
		legacyStorage().get(ACCESS_RIGHTS_KEY, (rights) => {
			if (win === current) legacyPortal.assortAllowed = rights?.ASSFO !== false;
		})
	);
}

export function refreshLegacyPortal() {
	const home = vmHome();
	assign('loaded', Boolean(home));
	if (!home) return;

	const apps = (safe(() => home.apps(), null) ?? []).map(plainApp);
	// home.js loginDone: the three application logons and the sales options are done (3 at once offline).
	const ready = safe(() => home.loginDone, 0) >= 3;
	assign(
		'title',
		toPlainText(
			safe(() => home.title(), ''),
			true
		)
	);
	assign('apps', apps);
	assign('news', (safe(() => home.news(), null) ?? []).map(plainNews).filter(Boolean));
	assign('ready', ready);

	if ((rightsReads === 0 && apps.length > 0) || (rightsReads === 1 && ready)) readAccessRights();
}

// home.js clickHandler(app, evt, blank) on the live model: the eCOS tile waits for the logons, then opens
// #ecos; Assort and BO v2 post their form to this window (WebAppIframe loadApp); PSP replaces the top window;
// external applications open in a new one (a user activation extends to same-origin descendant frames).
export function openLegacyPortalApplication(tile) {
	const home = vmHome();
	const app = safe(() => home.apps().find((item) => item.reference === tile?.reference && (tile.id == null || item.id === tile.id)));
	if (!app) return false;

	if (!tile.blank) setOpening(tile.reference);
	try {
		runWithVueDialogs(win, () => home.clickHandler(app, null, Boolean(tile.blank)));
		return true;
	} catch (error) {
		setOpening(null);
		log('error', 'legacyPortal', `Opening ${tile.reference} failed: ${error.message}`);
		return false;
	}
}

function setOpening(reference) {
	clearTimeout(openingTimer);
	openingTimer = reference ? setTimeout(() => setOpening(null), OPENING_TIMEOUT_MS) : null;
	legacyPortal.opening = reference;
}

// The router bridge's way into the application from the portal (legacyNavigationBridge handleLegacyPageChange).
export const enterLegacyApplication = () => openLegacyPortalApplication({ reference: ECOS_APP, id: null, blank: false });

function start() {
	if (!win) return;
	releaseDialogs ??= holdVueDialogs(win);
	refreshLegacyPortal();
	pollTimer ??= setInterval(refreshLegacyPortal, POLL_MS);
}

function stop() {
	clearInterval(pollTimer);
	pollTimer = null;
	releaseDialogs?.();
	releaseDialogs = null;
	setOpening(null);
}

// iframeWatcher: whether the legacy currently shows its portal page.
export function setLegacyPortalActive(isActive) {
	active = Boolean(isActive);
	if (active) start();
	else stop();
}

// Called by iframeWatcher once amplify and externis exist in a freshly loaded legacy document.
export function attachLegacyPortal(iframeWindow) {
	if (!iframeWindow || win === iframeWindow) return;
	if (win) detachLegacyPortal();
	win = iframeWindow;
	if (active) start();
}

// Called on every iframe load, before anything reads the new document.
export function detachLegacyPortal() {
	stop();
	win = null;
	active = false;
	rightsReads = 0;
	Object.assign(legacyPortal, initialState());
}
